import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, BadgeCheck } from "lucide-react";
import { SITE, NAV_LINKS, LOGO } from "@/content/site";
import { LOCATIONS } from "@/content/locations";

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-navy text-white">
    <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 sm:py-20">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-8">
        <div className="md:col-span-4">
          <Link to="/" data-testid="footer-logo-link" className="flex items-center gap-2.5">
            <img
              src={LOGO.chip}
              alt="Point of Pines Pools & Service logo"
              data-testid="footer-logo-image"
              className="w-12 h-12 rounded-full shadow-lg"
            />
            <span className="leading-tight">
              <span className="block font-display font-extrabold tracking-tight text-[17px]">Point of Pines</span>
              <span className="block text-[11px] font-mono uppercase tracking-[0.22em] text-sky2">Pools &amp; Service</span>
            </span>
          </Link>
          <p className="mt-5 text-sm text-slate-300 leading-relaxed max-w-xs">
            Locally owned and operated residential pool service for all of Pinellas County, Florida. Reliable weekly care, professional water chemistry, and honest communication.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-aqua">
            <BadgeCheck className="w-4 h-4" /> CPO Certified · #C-025145
          </div>
        </div>

        <div className="md:col-span-2">
          <h3 className="text-xs font-mono uppercase tracking-[0.22em] text-sky2 mb-4">Company</h3>
          <ul className="space-y-2.5">
            {NAV_LINKS.map((l) => (
              <li key={l.path}>
                <Link to={l.path} data-testid={`footer-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`} className="text-sm text-slate-300 hover:text-white transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h3 className="text-xs font-mono uppercase tracking-[0.22em] text-sky2 mb-4">Service Areas</h3>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5">
            {LOCATIONS.slice(0, 12).map((l) => (
              <li key={l.slug}>
                <Link to={`/service-areas/${l.slug}/`} data-testid={`footer-area-${l.slug}-link`} className="text-sm text-slate-300 hover:text-white transition-colors">
                  {l.name}
                </Link>
              </li>
            ))}
          </ul>
          <Link to="/service-areas/" data-testid="footer-all-areas-link" className="inline-block mt-4 text-sm font-semibold text-aqua hover:text-sky2 transition-colors">
            View all of Pinellas County
          </Link>
        </div>

        <div className="md:col-span-3">
          <h3 className="text-xs font-mono uppercase tracking-[0.22em] text-sky2 mb-4">Contact</h3>
          <ul className="space-y-3.5">
            <li>
              <a href={SITE.phoneHref} data-testid="footer-phone-link" className="flex items-center gap-3 text-sm text-slate-200 hover:text-white transition-colors">
                <Phone className="w-4 h-4 text-aqua shrink-0" /> {SITE.phone}
              </a>
            </li>
            <li>
              <a href={SITE.emailHref} data-testid="footer-email-link" className="flex items-center gap-3 text-sm text-slate-200 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-aqua shrink-0" /> {SITE.email}
              </a>
            </li>
            <li className="flex items-start gap-3 text-sm text-slate-300">
              <MapPin className="w-4 h-4 text-aqua shrink-0 mt-0.5" /> Serving all of Pinellas County, Florida
            </li>
          </ul>
          <Link
            to="/contact/"
            data-testid="footer-quote-button"
            className="mt-6 inline-flex items-center gap-2 bg-aqua text-navy text-sm font-bold px-5 py-3 rounded-full transition-all duration-300 hover:bg-sky2 hover:text-navy hover:-translate-y-0.5"
          >
            Request Pool Service
          </Link>
        </div>
      </div>

      <div className="mt-14 pt-7 border-t border-white/10">
        <p className="text-xs text-slate-400">
          &copy; {new Date().getFullYear()} Point of Pines Pools &amp; Service. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);
